'use client';
import React, { useState } from 'react';
import { Users, UserPlus, UserMinus, Shield, Key, LogOut, AlertTriangle, CheckCircle, XCircle, Search, Filter, RefreshCw, Lock, Unlock, Activity, Edit3, Power, ShieldAlert, Globe, Monitor, Smartphone } from 'lucide-react';
import MetricCard from '@/components/ui/MetricCard';
import StatusBadge from '@/components/ui/StatusBadge';
import SectionHeader from '@/components/ui/SectionHeader';
import Icon from '@/components/ui/AppIcon';


type ActiveTab = 'users' | 'roles' | 'permission-overrides' | 'sessions';

interface PlatformUser {
  id: string;
  name: string;
  email: string;
  role: string;
  department: string;
  status: 'active' | 'suspended' | 'deprovisioned' | 'pending';
  mfaEnabled: boolean;
  lastLogin: string;
  createdAt: string;
  tenant: string;
  sessionCount: number;
}

interface RolePermission {
  module: string;
  view: boolean;
  create: boolean;
  edit: boolean;
  delete: boolean;
  approve: boolean;
  export: boolean;
}

interface PermissionOverride {
  id: string;
  user: string;
  role: string;
  module: string;
  permission: string;
  overrideType: 'grant' | 'revoke';
  reason: string;
  approvedBy: string;
  appliedAt: string;
  expiresAt: string | null;
  status: 'active' | 'expired' | 'revoked';
}

interface ActiveSession {
  id: string;
  user: string;
  role: string;
  ip: string;
  device: string;
  browser: string;
  location: string;
  startedAt: string;
  lastActivity: string;
  duration: string;
  risk: 'low' | 'medium' | 'high';
}

const platformUsers: PlatformUser[] = [
  { id: 'USR-001', name: 'Alexandra Voss', email: 'a.voss@crowdvault.io', role: 'Super Admin', department: 'Platform', status: 'active', mfaEnabled: true, lastLogin: '2026-08-20 16:09', createdAt: '2022-01-15', tenant: 'CrowdVault HQ', sessionCount: 1 },
  { id: 'USR-002', name: 'Lena Okafor', email: 'l.okafor@crowdvault.io', role: 'Compliance Officer', department: 'Compliance', status: 'active', mfaEnabled: true, lastLogin: '2026-08-20 15:58', createdAt: '2023-03-08', tenant: 'CrowdVault HQ', sessionCount: 2 },
  { id: 'USR-003', name: 'Marcus Patel', email: 'm.patel@crowdvault.io', role: 'Finance Officer', department: 'Finance', status: 'active', mfaEnabled: true, lastLogin: '2026-08-20 14:22', createdAt: '2023-06-12', tenant: 'CrowdVault HQ', sessionCount: 1 },
  { id: 'USR-004', name: 'Dmitri Lefèvre', email: 'd.lefevre@meridian.io', role: 'Risk Officer', department: 'Risk', status: 'active', mfaEnabled: false, lastLogin: '2026-08-20 13:45', createdAt: '2024-02-20', tenant: 'Meridian Capital', sessionCount: 1 },
  { id: 'USR-005', name: 'Ryo Nakamura', email: 'r.nakamura@meridian.io', role: 'Campaign Manager', department: 'Operations', status: 'active', mfaEnabled: true, lastLogin: '2026-08-20 16:09', createdAt: '2024-04-01', tenant: 'Meridian Capital', sessionCount: 1 },
  { id: 'USR-006', name: 'Sarah Bergmann', email: 's.bergmann@crestwood.io', role: 'Campaign Manager', department: 'Operations', status: 'suspended', mfaEnabled: true, lastLogin: '2026-08-18 09:12', createdAt: '2024-07-15', tenant: 'Crestwood Alternatives', sessionCount: 0 },
  { id: 'USR-007', name: 'James Harrington', email: 'j.harrington@crowdvault.io', role: 'Finance Officer', department: 'Finance', status: 'active', mfaEnabled: true, lastLogin: '2026-08-20 14:58', createdAt: '2023-09-01', tenant: 'CrowdVault HQ', sessionCount: 1 },
  { id: 'USR-008', name: 'Priya Sharma', email: 'p.sharma@ashford.io', role: 'Compliance Analyst', department: 'Compliance', status: 'pending', mfaEnabled: false, lastLogin: 'Never', createdAt: '2026-08-20', tenant: 'Ashford Wealth', sessionCount: 0 },
];

const roles = ['Super Admin', 'Compliance Officer', 'Finance Officer', 'Risk Officer', 'Campaign Manager', 'Compliance Analyst', 'Auditor', 'Trustee', 'Read-Only'];

const rolePermissionsMatrix: Record<string, RolePermission[]> = {
  'Super Admin': [
    { module: 'Platform Admin', view: true, create: true, edit: true, delete: true, approve: true, export: true },
    { module: 'User Management', view: true, create: true, edit: true, delete: true, approve: true, export: true },
    { module: 'Compliance', view: true, create: true, edit: true, delete: false, approve: true, export: true },
    { module: 'Finance / Ledger', view: true, create: true, edit: true, delete: false, approve: true, export: true },
    { module: 'KYC / AML', view: true, create: true, edit: true, delete: false, approve: true, export: true },
    { module: 'Campaigns', view: true, create: true, edit: true, delete: true, approve: true, export: true },
  ],
  'Compliance Officer': [
    { module: 'Platform Admin', view: false, create: false, edit: false, delete: false, approve: false, export: false },
    { module: 'User Management', view: true, create: false, edit: false, delete: false, approve: false, export: false },
    { module: 'Compliance', view: true, create: true, edit: true, delete: false, approve: true, export: true },
    { module: 'Finance / Ledger', view: true, create: false, edit: false, delete: false, approve: false, export: true },
    { module: 'KYC / AML', view: true, create: true, edit: true, delete: false, approve: true, export: true },
    { module: 'Campaigns', view: true, create: false, edit: false, delete: false, approve: true, export: true },
  ],
  'Finance Officer': [
    { module: 'Platform Admin', view: false, create: false, edit: false, delete: false, approve: false, export: false },
    { module: 'User Management', view: false, create: false, edit: false, delete: false, approve: false, export: false },
    { module: 'Compliance', view: true, create: false, edit: false, delete: false, approve: false, export: false },
    { module: 'Finance / Ledger', view: true, create: true, edit: true, delete: false, approve: true, export: true },
    { module: 'KYC / AML', view: true, create: false, edit: false, delete: false, approve: false, export: false },
    { module: 'Campaigns', view: true, create: false, edit: false, delete: false, approve: false, export: true },
  ],
};

const permissionOverrides: PermissionOverride[] = [
  { id: 'OVR-001', user: 'Dmitri Lefèvre', role: 'Risk Officer', module: 'Finance / Ledger', permission: 'export', overrideType: 'grant', reason: 'Temporary access for Q3 audit preparation', approvedBy: 'A. Voss', appliedAt: '2026-08-15 09:00', expiresAt: '2026-09-15 09:00', status: 'active' },
  { id: 'OVR-002', user: 'Ryo Nakamura', role: 'Campaign Manager', module: 'KYC / AML', permission: 'view', overrideType: 'grant', reason: 'Campaign-specific investor KYC visibility required', approvedBy: 'L. Okafor', appliedAt: '2026-08-10 14:30', expiresAt: '2026-08-31 23:59', status: 'active' },
  { id: 'OVR-003', user: 'Sarah Bergmann', role: 'Campaign Manager', module: 'Finance / Ledger', permission: 'create', overrideType: 'revoke', reason: 'Compliance hold — pending investigation', approvedBy: 'A. Voss', appliedAt: '2026-08-18 11:00', expiresAt: null, status: 'active' },
  { id: 'OVR-004', user: 'James Harrington', role: 'Finance Officer', module: 'Compliance', permission: 'approve', overrideType: 'grant', reason: 'Acting compliance cover during L. Okafor leave', approvedBy: 'A. Voss', appliedAt: '2026-07-01 08:00', expiresAt: '2026-07-31 23:59', status: 'expired' },
  { id: 'OVR-005', user: 'Marcus Patel', role: 'Finance Officer', module: 'User Management', permission: 'view', overrideType: 'grant', reason: 'Finance audit of user access controls', approvedBy: 'A. Voss', appliedAt: '2026-08-19 10:00', expiresAt: '2026-08-22 10:00', status: 'active' },
];

const activeSessions: ActiveSession[] = [
  { id: 'SES-7F3A9B', user: 'Alexandra Voss', role: 'Super Admin', ip: '10.0.1.42', device: 'Desktop', browser: 'Chrome 127', location: 'London, UK', startedAt: '2026-08-20 15:44', lastActivity: '2026-08-20 16:12', duration: '28m', risk: 'low' },
  { id: 'SES-2C8D4E', user: 'Lena Okafor', role: 'Compliance Officer', ip: '10.0.1.11', device: 'Desktop', browser: 'Firefox 128', location: 'Lagos, NG', startedAt: '2026-08-20 14:30', lastActivity: '2026-08-20 16:10', duration: '1h 40m', risk: 'low' },
  { id: 'SES-9A1F2C', user: 'Marcus Patel', role: 'Finance Officer', ip: '10.0.1.88', device: 'Desktop', browser: 'Safari 17', location: 'Mumbai, IN', startedAt: '2026-08-20 13:55', lastActivity: '2026-08-20 16:08', duration: '2h 13m', risk: 'low' },
  { id: 'SES-4B7E3D', user: 'Dmitri Lefèvre', role: 'Risk Officer', ip: '185.42.12.x', device: 'Mobile', browser: 'Chrome Mobile', location: 'Paris, FR', startedAt: '2026-08-20 15:20', lastActivity: '2026-08-20 16:05', duration: '45m', risk: 'medium' },
  { id: 'SES-6D2A8F', user: 'Ryo Nakamura', role: 'Campaign Manager', ip: '10.0.2.14', device: 'Desktop', browser: 'Edge 127', location: 'Tokyo, JP', startedAt: '2026-08-20 16:00', lastActivity: '2026-08-20 16:11', duration: '11m', risk: 'low' },
  { id: 'SES-1E9C5B', user: 'James Harrington', role: 'Finance Officer', ip: '92.168.44.x', device: 'Desktop', browser: 'Chrome 127', location: 'New York, US', startedAt: '2026-08-20 14:00', lastActivity: '2026-08-20 15:58', duration: '1h 58m', risk: 'high' },
];

function PermCell({ value }: { value: boolean }) {
  return (
    <td className="table-td text-center">
      {value ? (
        <CheckCircle size={14} className="text-positive mx-auto" />
      ) : (
        <XCircle size={14} className="text-surface-4 mx-auto" />
      )}
    </td>
  );
}

export default function PlatformAdminUsers() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('users');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState('Super Admin');
  const [terminatingSession, setTerminatingSession] = useState<string | null>(null);

  const handleTerminateSession = (sessionId: string) => {
    setTerminatingSession(sessionId);
    setTimeout(() => setTerminatingSession(null), 1500);
  };

  const tabs: { key: ActiveTab; label: string; icon: React.ElementType; badge?: number }[] = [
    { key: 'users', label: 'User Provisioning', icon: Users, badge: 1 },
    { key: 'roles', label: 'Role Assignment Matrix', icon: Shield },
    { key: 'permission-overrides', label: 'Permission Override Log', icon: Key, badge: 5 },
    { key: 'sessions', label: 'Session Controls', icon: Monitor, badge: activeSessions.length },
  ];

  const filteredUsers = platformUsers.filter((u) =>
    !searchQuery ||
    u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.role.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">User Management</h1>
          <p className="text-sm text-muted-foreground mt-1">Platform Admin · Provisioning · RBAC · Session Controls · 20 Aug 2026</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-secondary text-xs gap-1.5"><RefreshCw size={12} /> Refresh</button>
          <button className="btn-primary text-xs gap-1.5"><UserPlus size={13} /> Provision User</button>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Total Users" value={String(platformUsers.length)} subValue="Across all tenants" variant="default" icon={Users} iconColor="text-primary" />
        <MetricCard label="Active Sessions" value={String(activeSessions.length)} subValue="Live authenticated sessions" variant="info" icon={Activity} iconColor="text-info" />
        <MetricCard label="Suspended Accounts" value="1" subValue="Pending compliance review" variant="warning" icon={Lock} iconColor="text-warning" />
        <MetricCard label="MFA Disabled" value="2" subValue="Require immediate enforcement" variant="critical" icon={ShieldAlert} iconColor="text-critical" />
      </div>

      {/* MFA Warning */}
      <div className="alert-banner alert-warning">
        <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-sm">2 Users Without MFA — Security Risk</p>
          <p className="text-xs text-muted-foreground mt-0.5">D. Lefèvre (Risk Officer) and P. Sharma (Compliance Analyst) have not enrolled in MFA. Enforce immediately per platform security policy.</p>
        </div>
        <button className="ml-auto flex-shrink-0 btn-ghost text-xs gap-1">Enforce MFA <ShieldAlert size={11} /></button>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-border overflow-x-auto">
        {tabs.map(({ key, label, icon: Icon, badge }) => (
          <button
            key={`user-tab-${key}`}
            onClick={() => setActiveTab(key)}
            className={`portal-tab flex items-center gap-1.5 ${activeTab === key ? 'active' : ''}`}
          >
            <Icon size={13} />{label}
            {badge !== undefined && (
              <span className="ml-1 text-xs bg-warning/20 text-warning rounded-full px-1.5 py-0.5 font-mono-data">{badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* User Provisioning Tab */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search users by name, email, or role..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm bg-surface-2 border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/40 text-foreground placeholder:text-muted-foreground"
              />
            </div>
            <button className="btn-secondary text-xs gap-1.5"><Filter size={12} /> Filter</button>
          </div>

          <div className="glass-card rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-3">
                  <tr>
                    {['User', 'Role', 'Tenant', 'Status', 'MFA', 'Last Login', 'Sessions', 'Actions'].map((h) => (
                      <th key={`th-usr-${h}`} className="table-th">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="data-table-row">
                      <td className="table-td">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">
                            {user.name.split(' ').map((n) => n[0]).join('')}
                          </div>
                          <div>
                            <p className="font-medium text-foreground text-sm">{user.name}</p>
                            <p className="text-xs font-mono-data text-muted-foreground">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="table-td">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${
                          user.role === 'Super Admin' ? 'bg-critical/10 text-critical border-critical/20' :
                          user.role === 'Compliance Officer' ? 'bg-warning/10 text-warning border-warning/20' :
                          user.role === 'Finance Officer'? 'bg-positive/10 text-positive border-positive/20' : 'bg-surface-4 text-muted-foreground border-border'
                        }`}>{user.role}</span>
                      </td>
                      <td className="table-td text-xs text-muted-foreground">{user.tenant}</td>
                      <td className="table-td">
                        <StatusBadge
                          status={user.status === 'active' ? 'approved' : user.status === 'suspended' ? 'flagged' : user.status === 'pending' ? 'pending' : 'rejected'}
                          label={user.status}
                        />
                      </td>
                      <td className="table-td">
                        {user.mfaEnabled ? (
                          <span className="flex items-center gap-1 text-xs text-positive font-semibold"><CheckCircle size={12} /> Enabled</span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs text-critical font-semibold"><XCircle size={12} /> Disabled</span>
                        )}
                      </td>
                      <td className="table-td text-xs font-mono-data text-muted-foreground">{user.lastLogin}</td>
                      <td className="table-td">
                        <span className={`font-mono-data text-sm font-bold ${user.sessionCount > 0 ? 'text-info' : 'text-muted-foreground'}`}>
                          {user.sessionCount}
                        </span>
                      </td>
                      <td className="table-td">
                        <div className="flex items-center gap-1">
                          <button className="btn-ghost text-xs gap-1 text-info"><Edit3 size={11} /> Edit</button>
                          {user.status === 'active' ? (
                            <button className="btn-ghost text-xs gap-1 text-warning"><Lock size={11} /> Suspend</button>
                          ) : user.status === 'suspended' ? (
                            <button className="btn-ghost text-xs gap-1 text-positive"><Unlock size={11} /> Restore</button>
                          ) : null}
                          {user.status !== 'deprovisioned' && (
                            <button className="btn-ghost text-xs gap-1 text-critical"><UserMinus size={11} /> Deprovision</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Role Assignment Matrix Tab */}
      {activeTab === 'roles' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <p className="text-sm text-muted-foreground">View permissions for role:</p>
            <div className="flex flex-wrap gap-2">
              {Object.keys(rolePermissionsMatrix).map((role) => (
                <button
                  key={role}
                  onClick={() => setSelectedRole(role)}
                  className={`text-xs px-3 py-1.5 rounded-lg font-semibold border transition-all ${
                    selectedRole === role
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-surface-2 text-muted-foreground border-border hover:border-primary/40'
                  }`}
                >
                  {role}
                </button>
              ))}
            </div>
          </div>

          <div className="glass-card rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <SectionHeader title={`${selectedRole} — Permission Matrix`} subtitle="Module-level access controls · RBAC enforced" />
              <button className="btn-secondary text-xs gap-1.5"><Edit3 size={11} /> Edit Role</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-3">
                  <tr>
                    <th className="table-th text-left">Module</th>
                    {['View', 'Create', 'Edit', 'Delete', 'Approve', 'Export'].map((h) => (
                      <th key={`th-perm-${h}`} className="table-th text-center">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(rolePermissionsMatrix[selectedRole] ?? []).map((perm) => (
                    <tr key={perm.module} className="data-table-row">
                      <td className="table-td font-medium text-foreground text-sm">{perm.module}</td>
                      <PermCell value={perm.view} />
                      <PermCell value={perm.create} />
                      <PermCell value={perm.edit} />
                      <PermCell value={perm.delete} />
                      <PermCell value={perm.approve} />
                      <PermCell value={perm.export} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* All roles summary */}
          <div className="glass-card rounded-xl p-4">
            <SectionHeader title="All Platform Roles" subtitle="Role hierarchy and user counts" />
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {roles.map((role) => {
                const count = platformUsers.filter((u) => u.role === role).length;
                return (
                  <div
                    key={role}
                    onClick={() => { if (rolePermissionsMatrix[role]) setSelectedRole(role); }}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-all cursor-pointer ${
                      rolePermissionsMatrix[role] ? 'hover:border-primary/40 hover:bg-surface-2' : 'opacity-60'
                    } border-border bg-surface-2`}
                  >
                    <div className="flex items-center gap-2">
                      <Shield size={14} className={role === 'Super Admin' ? 'text-critical' : role.includes('Officer') ? 'text-warning' : 'text-muted-foreground'} />
                      <span className="text-sm font-medium text-foreground">{role}</span>
                    </div>
                    <span className="text-xs font-mono-data text-muted-foreground">{count} user{count !== 1 ? 's' : ''}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Permission Override Audit Log Tab */}
      {activeTab === 'permission-overrides' && (
        <div className="space-y-4">
          <div className="alert-banner">
            <Key size={16} className="flex-shrink-0 text-warning" />
            <div>
              <p className="font-semibold text-sm">Permission Override Audit Log</p>
              <p className="text-xs text-muted-foreground mt-0.5">All permission overrides are immutably logged. Overrides require dual approval and are time-bound by policy.</p>
            </div>
          </div>

          <div className="glass-card rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <SectionHeader title="Override Log" subtitle={`${permissionOverrides.length} overrides · ${permissionOverrides.filter((o) => o.status === 'active').length} active`} />
              <button className="btn-primary text-xs gap-1.5"><Key size={11} /> New Override</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-3">
                  <tr>
                    {['Override ID', 'User', 'Module', 'Permission', 'Type', 'Reason', 'Approved By', 'Applied', 'Expires', 'Status'].map((h) => (
                      <th key={`th-ovr-${h}`} className="table-th">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {permissionOverrides.map((ovr) => (
                    <tr key={ovr.id} className="data-table-row">
                      <td className="table-td font-mono-data text-xs text-muted-foreground">{ovr.id}</td>
                      <td className="table-td">
                        <p className="font-medium text-foreground text-sm">{ovr.user}</p>
                        <p className="text-xs text-muted-foreground">{ovr.role}</p>
                      </td>
                      <td className="table-td text-xs text-foreground">{ovr.module}</td>
                      <td className="table-td">
                        <span className="text-xs font-mono-data font-semibold text-foreground capitalize">{ovr.permission}</span>
                      </td>
                      <td className="table-td">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${
                          ovr.overrideType === 'grant' ?'bg-positive/10 text-positive border-positive/20' :'bg-critical/10 text-critical border-critical/20'
                        }`}>
                          {ovr.overrideType === 'grant' ? '+ Grant' : '− Revoke'}
                        </span>
                      </td>
                      <td className="table-td text-xs text-muted-foreground max-w-[180px] truncate" title={ovr.reason}>{ovr.reason}</td>
                      <td className="table-td text-xs text-muted-foreground">{ovr.approvedBy}</td>
                      <td className="table-td text-xs font-mono-data text-muted-foreground">{ovr.appliedAt}</td>
                      <td className="table-td text-xs font-mono-data text-muted-foreground">{ovr.expiresAt ?? 'Permanent'}</td>
                      <td className="table-td">
                        <StatusBadge
                          status={ovr.status === 'active' ? 'approved' : ovr.status === 'expired' ? 'rejected' : 'pending'}
                          label={ovr.status}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Session Termination Controls Tab */}
      {activeTab === 'sessions' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <span className="pulse-dot" />
              <span className="text-sm font-semibold text-foreground">{activeSessions.length} Active Sessions</span>
              <span className="text-xs text-muted-foreground">· Real-time monitoring</span>
            </div>
            <button className="btn-ghost text-xs gap-1.5 text-critical border border-critical/30 hover:bg-critical/5 px-3 py-1.5 rounded-lg">
              <Power size={12} /> Terminate All Sessions
            </button>
          </div>

          <div className="glass-card rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-3">
                  <tr>
                    {['Session ID', 'User', 'IP Address', 'Device', 'Location', 'Started', 'Last Activity', 'Duration', 'Risk', 'Action'].map((h) => (
                      <th key={`th-ses-${h}`} className="table-th">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {activeSessions.map((session) => (
                    <tr key={session.id} className="data-table-row">
                      <td className="table-td font-mono-data text-xs text-muted-foreground">{session.id}</td>
                      <td className="table-td">
                        <p className="font-medium text-foreground text-sm">{session.user}</p>
                        <p className="text-xs text-muted-foreground">{session.role}</p>
                      </td>
                      <td className="table-td font-mono-data text-xs text-muted-foreground">{session.ip}</td>
                      <td className="table-td">
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          {session.device === 'Mobile' ? <Smartphone size={12} /> : <Monitor size={12} />}
                          <span>{session.browser}</span>
                        </div>
                      </td>
                      <td className="table-td">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Globe size={11} />
                          {session.location}
                        </div>
                      </td>
                      <td className="table-td text-xs font-mono-data text-muted-foreground">{session.startedAt}</td>
                      <td className="table-td text-xs font-mono-data text-muted-foreground">{session.lastActivity}</td>
                      <td className="table-td font-mono-data text-sm font-semibold text-foreground">{session.duration}</td>
                      <td className="table-td">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${
                          session.risk === 'low' ? 'bg-positive/10 text-positive border-positive/20' :
                          session.risk === 'medium'? 'bg-warning/10 text-warning border-warning/20' : 'bg-critical/10 text-critical border-critical/20'
                        }`}>
                          {session.risk.charAt(0).toUpperCase() + session.risk.slice(1)}
                        </span>
                      </td>
                      <td className="table-td">
                        {terminatingSession === session.id ? (
                          <span className="text-xs text-positive flex items-center gap-1"><CheckCircle size={11} /> Terminated</span>
                        ) : (
                          <button
                            onClick={() => handleTerminateSession(session.id)}
                            className="flex items-center gap-1 text-xs text-critical font-semibold border border-critical/30 px-2 py-1 rounded-lg hover:bg-critical/5 transition-all"
                          >
                            <LogOut size={11} /> Terminate
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Session Risk Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(['low', 'medium', 'high'] as const).map((risk) => {
              const count = activeSessions.filter((s) => s.risk === risk).length;
              return (
                <div key={risk} className={`glass-card rounded-xl p-4 border ${
                  risk === 'low' ? 'border-positive/20' : risk === 'medium' ? 'border-warning/20' : 'border-critical/20'
                }`}>
                  <p className={`text-xs font-semibold uppercase tracking-widest mb-1 ${
                    risk === 'low' ? 'text-positive' : risk === 'medium' ? 'text-warning' : 'text-critical'
                  }`}>{risk} Risk Sessions</p>
                  <p className="text-3xl font-bold font-mono-data text-foreground">{count}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {risk === 'high' ? 'Immediate review recommended' : risk === 'medium' ? 'Monitor closely' : 'Normal activity'}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
